import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Phone,
  Calendar,
  Star,
  Car,
  FileText,
  Shield,
  UserCog,
  Smartphone,
  AlertCircle,
  Navigation,
  History,
  Building2,
  Save,
  X,
  User,
  RotateCcw,
  UserX,
} from 'lucide-react';
import { api } from '../../utils/api';
import { confirmPermanentDelete } from '../../utils/confirmPermanentDelete';
import { useAuth } from '../../contexts/AuthContext';
import DriverMap from '../../components/DriverMap';
import BlockDriverModal from '../../components/BlockDriverModal';
import HistoryModal from '../../components/HistoryModal';
import DocumentViewerModal from '../../components/DocumentViewerModal';

// Role values as they exist in the data. 'actingdriver' is the older spelling and
// is read-only here - selecting Acting Driver always writes 'acting_driver'.
const DRIVER_ROLE_LABELS = {
  dco: 'DCO',
  salaried: 'Salaried',
  acting_driver: 'Acting Driver',
  actingdriver: 'Acting Driver'
};

const DRIVER_ROLE_OPTIONS = [
  { value: 'dco', label: 'DCO' },
  { value: 'acting_driver', label: 'Acting Driver' },
  { value: 'salaried', label: 'Salaried' }
];

// Which service(s) a driver actually performs — RIDE (brings their own
// vehicle) and/or ACTING (drives the customer's). Separate from `role` above,
// which is a settlement classification and says nothing about this.
const SERVICE_TYPE_OPTIONS = [
  { value: 'RIDE', label: 'Ride' },
  { value: 'ACTING', label: 'Acting' }
];

const DriverDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showRegionalOfficeModal, setShowRegionalOfficeModal] = useState(false);
  /*
      Holds a Ride opt-in that is waiting on the operator to confirm, as
      { next, reason }. Null when nothing is pending. The checkbox renders from
      `driver.serviceTypes`, so leaving the mutation unsent is all that Cancel
      needs to do — the tick reverts on the next render by itself.
  */
  const [pendingRideOptIn, setPendingRideOptIn] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [showDriverJsonModal, setShowDriverJsonModal] = useState(false);
  const [isEditingDueDate, setIsEditingDueDate] = useState(false);
  const [newDueDate, setNewDueDate] = useState('');

  useEffect(() => {
    const anyModalOpen = showDriverJsonModal;
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showDriverJsonModal]);

  // Handle document viewing with presigned URL
  const handleViewDocument = async (documentType) => {
    try {
      setDocumentLoading(true);
      const response = await api.get(`/drivers/${id}/document/${documentType}`);
      const { presignedUrl } = response.data.data;

      // Set document data and open modal
      setCurrentDocument({
        url: presignedUrl,
        type: documentType,
        originalUrl: response.data.data.originalUrl
      });
      setShowDocumentModal(true);
    } catch (error) {
      console.error('Error fetching document:', error);
      toast.error('Failed to load document');
    } finally {
      setDocumentLoading(false);
    }
  };

  // Handle closing document modal
  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setCurrentDocument(null);
  };

  // Handle ESC key to close modal
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showDocumentModal) {
        handleCloseDocumentModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDocumentModal]);

  const { data, isLoading, error, refetch } = useQuery(
    ['driver', id],
    () => api.get(`/drivers/${id}`),
    {
      enabled: !!id
    }
  );

  // Extract driver data
  const driverData = data?.data?.data || data?.data;
  const driver = driverData?.driver || driverData;

  // Fetch current trip details if driver has a current trip
  const { data: currentTripData } = useQuery(
    ['trip', driver?.currentTripId],
    () => api.get(`/trips/${driver.currentTripId}`),
    {
      enabled: !!driver?.currentTripId,
      select: (data) => data?.data?.data?.trip || data?.data?.trip
    }
  );

  // Fetch regional offices for super admin
  const { data: regionalOffices } = useQuery(
    ['regional-offices'],
    () => api.get('/regional-offices'),
    {
      enabled: user?.role === 'SUPER_ADMIN',
      select: (data) => data?.data?.data?.offices || data?.data?.offices || []
    }
  );

  // Fetch current regional office for this driver
  const { data: currentRegionalOffice } = useQuery(
    ['regional-office', driver?.regionalOffice],
    () => api.get(`/regional-offices/${driver?.regionalOffice}`),
    {
      enabled: !!driver?.regionalOffice && user?.role === 'SUPER_ADMIN',
      select: (data) => data?.data?.data || data?.data
    }
  );

  const approveMutation = useMutation(
    () => {
      const endpoint = (user?.role === 'ADMIN' || user?.role === 'admin')
        ? `/drivers/admin/${id}/approve`
        : `/drivers/${id}/approve`;
      return api.put(endpoint);
    },
    {
      onSuccess: () => {
        toast.success('Driver approved successfully');
        refetch();
      },
      onError: () => {
        toast.error('Failed to approve driver');
      }
    }
  );

  const blockMutation = useMutation(
    (blockData) => api.put(`/drivers/${id}/block`, blockData),
    {
      onSuccess: (_, blockData) => {
        toast.success(`Driver ${blockData.isBlocked ? 'blocked' : 'unblocked'} successfully`);
        refetch();
      },
      onError: () => {
        toast.error('Failed to update driver status');
      }
    }
  );

  // A deleted driver is also un-approved and unavailable. Restoring brings the account (and
  // the vehicle the deletion removed) back, but the driver must be approved again.
  const restoreMutation = useMutation(
    () => api.put(`/drivers/${id}/restore-account`),
    {
      onSuccess: () => {
        toast.success('Driver account restored. Approve the driver to let them take trips.');
        refetch();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || 'Failed to restore driver account');
      }
    }
  );

  const markDeletedMutation = useMutation(
    () => api.put(`/drivers/${id}/mark-deleted`, {}),
    {
      onSuccess: () => {
        toast.success('Driver account marked as deleted');
        refetch();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || 'Failed to mark driver account as deleted');
      }
    }
  );

  const deleteMutation = useMutation(
    () => api.delete(`/drivers/${id}`),
    {
      onSuccess: () => {
        toast.success('Driver deleted successfully');
        navigate('/drivers');
      },
      onError: () => {
        toast.error('Failed to delete driver');
      }
    }
  );

  // Mutation for updating driver's regional office
  const updateRegionalOfficeMutation = useMutation(
    (regionalOfficeId) => api.put(`/drivers/${id}/regional-office`, { regionalOffice: regionalOfficeId }),
    {
      onSuccess: () => {
        toast.success('Driver regional office updated successfully');
        queryClient.invalidateQueries(['driver', id]);
        queryClient.invalidateQueries(['regional-office', driver?.regionalOffice]);
        setShowRegionalOfficeModal(false);
      },
      onError: () => {
        toast.error('Failed to update driver regional office');
      }
    }
  );

  // Mutation for updating driver details
  const updateDriverMutation = useMutation(
    (driverData) => api.put(`/drivers/${id}/details`, driverData),
    {
      onSuccess: () => {
        toast.success('Driver details updated successfully');
        queryClient.invalidateQueries(['driver', id]);
        setIsEditing(false);
        setEditingDriver(null);
      },
      onError: () => {
        toast.error('Failed to update driver details');
      }
    }
  );

  // Mutation for updating developer flag
  const updateDeveloperMutation = useMutation(
    (devFlag) => api.put(`/drivers/${id}/developer`, { dev: devFlag }),
    {
      onSuccess: (data, devFlag) => {
        toast.success(`Driver developer flag ${devFlag ? 'enabled' : 'disabled'} successfully`);
        queryClient.invalidateQueries(['driver', id]);
      },
      onError: () => {
        toast.error('Failed to update developer flag');
      }
    }
  );

  // In-house vs partner. Drives NOT Premium discount eligibility + premium
  // ride prioritization on the matching engine.
  const updateCategoryMutation = useMutation(
    (category) => api.patch(`/drivers/${id}/category`, { category }),
    {
      onSuccess: (data, category) => {
        toast.success(`Driver marked as ${category === 'IN_HOUSE' ? 'In-house' : 'Partner'}`);
        queryClient.invalidateQueries(['driver', id]);
      },
      onError: () => {
        toast.error('Failed to update driver category');
      }
    }
  );

  // Driver role (DCO / acting driver / salaried). Single-field $set on the
  // server so legacy driver docs don't fail whole-document validation.
  const updateRoleMutation = useMutation(
    (role) => api.patch(`/drivers/${id}/role`, { role }),
    {
      onSuccess: (data, role) => {
        toast.success(`Role updated to ${DRIVER_ROLE_LABELS[role] || role}`);
        queryClient.invalidateQueries(['driver', id]);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to update driver role');
      }
    }
  );

  // Which service(s) a driver offers (RIDE / ACTING). The app-side opt-in flow
  // only ever ADDS to this array, so this is the only way to correct a driver
  // whose serviceTypes were granted incorrectly (e.g. an acting-only driver
  // who also ended up with RIDE) without a manual DB edit.
  const updateServiceTypesMutation = useMutation(
    (serviceTypes) => api.patch(`/drivers/${id}/service-types`, { serviceTypes }),
    {
      onSuccess: (data, serviceTypes) => {
        toast.success(`Service type updated to ${serviceTypes.join(', ')}`);
        queryClient.invalidateQueries(['driver', id]);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || 'Failed to update driver service types');
      }
    }
  );

  // Mutation for updating next due date
  const updateDueDateMutation = useMutation(
    (dueDate) => api.put(`/drivers/${id}/due-date`, { nextDueDate: dueDate }),
    {
      onSuccess: () => {
        toast.success('Next due date updated successfully');
        queryClient.invalidateQueries(['driver', id]);
        setIsEditingDueDate(false);
        setNewDueDate('');
      },
      onError: () => {
        toast.error('Failed to update next due date');
      }
    }
  );

  const updateAvailMutation = useMutation(
    (availFlag) => api.put(`/drivers/${id}/availability`, { isAvailable: availFlag }),
    {
      onSuccess: (data, availFlag) => {
        toast.success(`Driver availability ${availFlag ? 'enabled' : 'disabled'} successfully`);
        queryClient.invalidateQueries(['driver', id]);
      },
      onError: () => {
        toast.error('Failed to update availability');
      }
    }
  );

  const handleApprove = () => {
    approveMutation.mutate();
  };

  const handleBlock = (blockData) => {
    blockMutation.mutate(blockData);
  };

  const handleDelete = () => {
    if (confirmPermanentDelete('this driver record and their vehicle', 'To keep the record, use "Mark as deleted" instead.')) {
      deleteMutation.mutate();
    }
  };

  const handleRestore = () => {
    if (window.confirm('Restore this account? The driver must be approved again before taking trips.')) {
      restoreMutation.mutate();
    }
  };

  const handleMarkDeleted = () => {
    if (window.confirm('Mark this account as deleted? The driver will be un-approved and unavailable. You can restore it later.')) {
      markDeletedMutation.mutate();
    }
  };

  const handleUpdateRegionalOffice = (regionalOfficeId) => {
    updateRegionalOfficeMutation.mutate(regionalOfficeId);
  };

  const handleEditDriver = () => {
    setEditingDriver({
      name: driver.name,
      phone: driver.phone,
      email: driver.email,
      aadharNo: driver.aadharNo,
      licenseNo: driver.licenseNo,
      panNo: driver.panNo,
      gender: driver.gender
    });
    setIsEditing(true);
  };

  const handleSaveDriver = () => {
    if (editingDriver) {
      updateDriverMutation.mutate(editingDriver);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingDriver(null);
  };

  const handleInputChange = (field, value) => {
    setEditingDriver(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggleDeveloper = () => {
    const newDevFlag = !driver.dev;
    updateDeveloperMutation.mutate(newDevFlag);
  };

  const handleToggleCategory = () => {
    // Unset reads as Partner, matching the backend: SubscriptionService.isInHouseDriver
    // compares `driverCategory || ''` against IN_HOUSE, so a driver with no category
    // set is not in-house and earns no discount. Defaulting the display to In-house
    // meant this toggle wrote PARTNER on its first click and appeared to change
    // something while the driver's actual eligibility stayed exactly as it was.
    const isInHouse = driver.driverCategory === 'IN_HOUSE';
    updateCategoryMutation.mutate(isInHouse ? 'PARTNER' : 'IN_HOUSE');
  };

  const handleToggleAvail = () => {
    const newAvailFlag = !driver.isAvailable;
    updateAvailMutation.mutate(newAvailFlag);
  };

  const handleEditDueDate = () => {
    const currentDate = new Date(driver.nextDueDate).toISOString().split('T')[0];
    setNewDueDate(currentDate);
    setIsEditingDueDate(true);
  };

  const handleSaveDueDate = () => {
    const selectedDate = new Date(newDueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error('Please select a future date');
      return;
    }

    updateDueDateMutation.mutate(newDueDate);
  };

  const handleCancelDueDate = () => {
    setIsEditingDueDate(false);
    setNewDueDate('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <AlertCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Driver not found</h3>
        <p className="empty-state-description">
          The driver you're looking for doesn't exist.
        </p>
        <Link to="/drivers" className="btn btn-primary mt-4">
          Back to Drivers
        </Link>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="empty-state">
        <AlertCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Driver not found</h3>
        <p className="empty-state-description">
          The driver you're looking for doesn't exist.
        </p>
        <Link to="/drivers" className="btn btn-primary mt-4">
          Back to Drivers
        </Link>
      </div>
    );
  }


  // Approved to drive, but not yet vetted for acting jobs — see the Approve button.
  const actingApprovalPending = Array.isArray(driver?.serviceTypes)
    && driver.serviceTypes.includes('ACTING')
    && !driver?.actingProfile?.isApproved;

  /*
      What this driver's service types MEAN, not what the document literally holds.

      Only ActingDriverProfile.optIn ever writes `serviceTypes`, so a driver who
      never opted into acting has no such field at all — and the backend reads that
      absence as ride-only (ActingDriverProfile.defaultServiceTypes returns
      ['RIDE']). Rendering the raw field therefore showed Ride UNTICKED for every
      ordinary ride driver, which reads as "this driver has no services" when they
      are in fact a perfectly normal ride driver.

      It also made edits start from an empty array, so ticking Acting on such a
      driver produced ['ACTING'] and silently dropped the Ride they implicitly had.
      Deriving the effective value fixes both: the boxes show the truth, and
      ticking Acting yields ['RIDE','ACTING'].

      Display only — nothing here writes to the driver document.
  */
  const hasExplicitServiceTypes = Array.isArray(driver?.serviceTypes)
    && driver.serviceTypes.length > 0;
  const effectiveServiceTypes = hasExplicitServiceTypes ? driver.serviceTypes : ['RIDE'];

  /*
      Was this driver's vehicle entered by hand because the RC check did not pass?

      updateDriverVehicleInfo deliberately does NOT reject an unverifiable plate —
      the early return that would have is commented out — it records
      `isParivahanFailed` on the vehicle and leaves the decision to a human. That
      makes THIS approval the only control standing between a made-up number plate
      and a driver taking trips.

      The Vehicle page already shows this, but approvals happen here, so an
      operator could approve without ever seeing that the plate was unverified.
      Surfaced beside the button that matters.

      Not proof of fraud: an MParivahan outage flags genuine vehicles the same
      way. It means "not automatically verified — check the RC document".
  */
  const vehicleParivahanFailed = driver?.vehicleId
    && typeof driver.vehicleId === 'object'
    && driver.vehicleId.isParivahanFailed === true;

  const getStatusBadge = () => {
    if (driver.isDeleted === true) {
      return <span className="badge badge-danger">Deleted</span>;
    }
    if (driver.isBlocked) {
      return <span className="badge badge-danger">Blocked</span>;
    }
    if (!driver.isApproved) {
      return <span className="badge badge-warning">Pending Approval</span>;
    }

    // Check if driver is in a trip based on trip status
    if (driver.currentTripId && currentTripData) {
      const tripStatus = currentTripData.status;

      // Show "In Trip" for ACCEPTED and PICKEDUP statuses
      if (tripStatus === 'ACCEPTED' || tripStatus === 'PICKEDUP') {
        return (
          <div className="flex flex-col space-y-1">
            <span className="badge badge-primary">In Trip</span>
            <Link
              to={`/trips/${driver.currentTripId}?from=driver&sourceId=${driver._id}&originalSource=${new URLSearchParams(window.location.search).get('from') || 'drivers'}`}
              className="text-xs text-primary-600 hover:text-primary-800 underline"
            >
              View Current Trip
            </Link>
          </div>
        );
      }

      // Show "Last Trip" for other completed statuses
      if (tripStatus === 'DROPPED' || tripStatus === 'PAYMENT_COMPLETED' || tripStatus === 'COMPLETED') {
        return (
          <div className="flex flex-col space-y-1">
            <span className="badge badge-info">Last Trip</span>
            <Link
              to={`/trips/${driver.currentTripId}?from=driver&sourceId=${driver._id}&originalSource=${new URLSearchParams(window.location.search).get('from') || 'drivers'}`}
              className="text-xs text-primary-600 hover:text-primary-800 underline"
            >
              View Last Trip
            </Link>
          </div>
        );
      }
    }

    if (driver.driverStatus?.status === 'offline') {
      return <span className="badge badge-info">Offline</span>;
    }
    return <span className="badge badge-success">Online</span>;
  };

  const getAvailBadge = () => {
    if (driver.isAvailable) {
      return <span className="badge badge-success">Available</span>;
    }
    return <span className="badge badge-danger">Not Available</span>;
  }

  const getUpdatedAtBadge = () => {
    if (driver.driverStatus?.updatedOn) {
      return <span className="badge border text-xs">{new Date(driver.driverStatus?.updatedOn).toLocaleDateString("en-IN", {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      })}</span>;
    }
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center lg:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  const searchParams = new URLSearchParams(window.location.search);
                  const fromSource = searchParams.get('from');
                  const sourceId = searchParams.get('sourceId');
                  const fromPath = location.state?.from;

                  // Navigate back to the source page
                  if (fromPath) {
                    navigate(fromPath);
                  } else if (fromSource === 'trip' && sourceId) {

                    navigate(`/trips/${sourceId}`);
                  } else if (fromSource === 'due-details' && sourceId) {
                    const targetUrl = `/financial/due/${sourceId}`;

                    navigate(targetUrl);
                  } else if (fromSource === 'gst-details') {

                    navigate('/financial/gst');
                  } else if (fromSource === 'pending') {

                    navigate('/drivers/pending');
                  } else {

                    navigate('/drivers');
                  }
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="page-title">{driver.name}</h1>
                <p className="page-subtitle">Driver Details</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {hasPermission('drivers', 'write') && (
                <button
                  onClick={handleToggleAvail}
                  disabled={updateAvailMutation.isLoading}
                  className={`btn flex items-center gap-2 ${driver.isAvailable
                    ? "btn-success"
                    : "btn-secondary text-gray-500"
                    }`}
                >
                  {/* Status Dot */}
                  <span
                    className={`w-2 h-2 rounded-full ${driver.isAvailable ? "bg-white animate-pulse" : "bg-gray-400"
                      }`}
                  />

                  {/* Command Label */}
                  <span>
                    {driver.isAvailable ? "Available" : "Not Available"}
                  </span>

                  {/* Loading */}
                  {updateAvailMutation.isLoading && (
                    <span className="loading loading-spinner loading-xs text-white"></span>
                  )}
                </button>

              )}
              {hasPermission('drivers', 'write') && (
                <button
                  onClick={handleToggleDeveloper}
                  disabled={updateDeveloperMutation.isLoading}
                  className={`
    relative flex items-center gap-3 pl-2 pr-4 py-1 rounded-full transition-all w-auto bg-gray-200
    ${updateDeveloperMutation.isLoading ? "opacity-60 cursor-wait" : "cursor-pointer"}
    ${driver.dev ? "border" : "border"}
  `}
                >
                  {/* Spinner */}
                  {updateDeveloperMutation.isLoading && (
                    <span className="loading loading-spinner loading-sm"></span>
                  )}

                  {/* Toggle Track */}
                  <span
                    className={`
      relative inline-flex items-center w-12 h-6 rounded-full shadow-inner
       ${driver.dev ? "bg-green-700" : "bg-gray-600"}
    `}
                  >
                    {/* Toggle Circle */}
                    <span
                      className={`
        absolute w-5 h-5 rounded-full shadow-md transform transition-all
        ${driver.dev ? "translate-x-6 bg-white" : "translate-x-1 bg-white"}
      `}
                    ></span>
                  </span>

                  {/* Label */}
                  <span className="text-sm font-medium text-black whitespace-nowrap">
                    {driver.dev ? "Developer" : "Not Developer"}
                  </span>
                </button>
              )}
              {hasPermission('drivers', 'write') && (
                <button
                  onClick={handleToggleCategory}
                  disabled={updateCategoryMutation.isLoading}
                  title="In-house drivers earn the customer's NOT Premium 50% discount and are offered premium rides first"
                  className={`
    relative flex items-center gap-3 pl-2 pr-4 py-1 rounded-full transition-all w-auto bg-gray-200 border
    ${updateCategoryMutation.isLoading ? "opacity-60 cursor-wait" : "cursor-pointer"}
  `}
                >
                  {/* Spinner */}
                  {updateCategoryMutation.isLoading && (
                    <span className="loading loading-spinner loading-sm"></span>
                  )}

                  {/* Toggle Track */}
                  <span
                    className={`
      relative inline-flex items-center w-12 h-6 rounded-full shadow-inner
       ${driver.driverCategory === 'IN_HOUSE' ? "bg-green-700" : "bg-gray-600"}
    `}
                  >
                    {/* Toggle Circle */}
                    <span
                      className={`
        absolute w-5 h-5 rounded-full shadow-md transform transition-all
        ${driver.driverCategory === 'IN_HOUSE' ? "translate-x-6 bg-white" : "translate-x-1 bg-white"}
      `}
                    ></span>
                  </span>

                  {/* Label */}
                  <span className="text-sm font-medium text-black whitespace-nowrap">
                    {driver.driverCategory === 'IN_HOUSE' ? "In-house" : "Partner"}
                  </span>
                </button>
              )}
              <button
                onClick={() => setShowHistoryModal(true)}
                className="btn btn-secondary"
              >
                <History className="h-4 w-4 mr-2" />
                History
              </button>

              {driver.location && (
                <Link
                  to={`/drivers/${driver._id}/location?from=drivers&sourceId=${driver._id}&originalSource=${new URLSearchParams(window.location.search).get('from') || 'drivers'}`}
                  className="btn btn-info"
                >
                  <Navigation className="h-4 w-4 mr-2" />
                  Track Location
                </Link>
              )}

              {/* Super Admin Regional Office Management */}
              {user?.role === 'SUPER_ADMIN' && (
                <button
                  onClick={() => setShowRegionalOfficeModal(true)}
                  className="btn btn-primary"
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Regional Office
                </button>
              )}

              {/* Super Admin Edit Driver */}
              {user?.role === 'SUPER_ADMIN' && (
                <button
                  onClick={handleEditDriver}
                  className="btn btn-secondary"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Driver
                </button>
              )}

              {vehicleParivahanFailed && (!driver.isApproved || actingApprovalPending) && (
                <span
                  title="This vehicle's registration number could not be verified with MParivahan. Check the RC document before approving."
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200"
                >
                  <AlertCircle className="h-3.5 w-3.5" />
                  RC not verified
                </span>
              )}

              {hasPermission('drivers', 'write') && driver.isDeleted !== true && (!driver.isApproved || actingApprovalPending) && (
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isLoading}
                  className="btn btn-success"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {/*
                      A driver approved to drive can still be waiting on the ACTING
                      capability — they are vetted separately, because an acting driver
                      drives the customer's own car. Hiding the button whenever the
                      top-level flag was set left no way to grant it.
                  */}
                  {driver.isApproved ? 'Approve Acting Driver' : 'Approve'}
                </button>
              )}

              {hasPermission('drivers', 'write') && (
                <button
                  onClick={() => setShowBlockModal(true)}
                  disabled={blockMutation.isLoading}
                  className={`btn ${driver.isBlocked ? 'btn-success' : 'btn-warning'}`}
                >
                  {driver.isBlocked ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Unblock
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Block
                    </>
                  )}
                </button>
              )}

              {hasPermission('drivers', 'write') && driver.isDeleted === true && (
                <button
                  onClick={handleRestore}
                  disabled={restoreMutation.isLoading}
                  className="btn btn-success"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Restore account
                </button>
              )}

              {hasPermission('drivers', 'delete') && driver.isDeleted !== true && (
                <button
                  onClick={handleMarkDeleted}
                  disabled={markDeletedMutation.isLoading}
                  className="btn btn-warning"
                  title="Un-approves the driver and blocks login. Can be restored."
                >
                  <UserX className="h-4 w-4 mr-2" />
                  Mark as deleted
                </button>
              )}

              {hasPermission('drivers', 'delete') && (
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isLoading}
                  className="btn btn-danger"
                  title="Removes the driver and their vehicle for good"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete permanently
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Account deletion */}
      {driver.isDeleted === true && (
        <div className="card border border-red-200">
          <div className="card-header">
            <h3 className="text-lg font-medium text-red-700">Account deleted</h3>
          </div>
          <div className="card-body">
            <p className="text-sm text-gray-700 mb-3">
              This driver deleted their account, so they are un-approved and unavailable.
              Restoring brings the account (and the vehicle the deletion removed) back; the
              driver must then be approved again before taking trips.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Deleted on</label>
                <p className="text-sm text-gray-900">
                  {driver.deletedAt ? new Date(driver.deletedAt).toLocaleString() : 'Unknown'}
                </p>
              </div>
              <div>
                <label className="form-label">Reason</label>
                <p className="text-sm text-gray-900">{driver.deleteReason || 'Not given'}</p>
              </div>
              <div>
                <label className="form-label">Deleted by</label>
                <p className="text-sm text-gray-900">{driver.deletedBy || 'The driver, in the app'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {driver.isDeleted !== true && driver.deletionRevokedAt && (
        <div className="text-sm text-gray-500">
          Account deletion was revoked on {new Date(driver.deletionRevokedAt).toLocaleString()}
          {driver.deletionRevokedBy ? ` by ${driver.deletionRevokedBy}` : ''}.
        </div>
      )}

      {/* Driver Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              {/* <button
                onClick={() => setShowDriverJsonModal(true)}
                className='font-medium bg-gray-100 px-2 rounded border'
              >
                {"{}"}
              </button> */}
            </div>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center space-x-3">
              <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-xl font-medium text-primary-700">
                  {driver.name ? driver.name.charAt(0).toUpperCase() : '?'}
                </span>
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">
                  {driver.name}
                </h4>

                <p className="text-sm text-gray-500">
                  {driver.licenseNo}
                </p>

                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {getStatusBadge()}
                  {getAvailBadge()}
                </div>

                <p className="text-sm text-gray-700 mt-3">
                  Status updated at {getUpdatedAtBadge()}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">{driver.phone}</span>
              </div>

              <div className="flex items-center space-x-3">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  Joined {new Date(driver.createdOn).toLocaleDateString()}
                </span>
              </div>

              <div className="flex items-center space-x-3">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  Gender: {driver.gender ? driver.gender.charAt(0).toUpperCase() + driver.gender.slice(1) : 'Not specified'}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Shield className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  Aadhar: {driver.aadharNo}
                </span>
              </div>

              <div className="flex items-center space-x-3">
                <FileText className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">
                  PAN: {driver.panNo}
                </span>
              </div>

              {/* Driver role - viewable by all, editable with drivers:write */}
              <div className="flex items-center space-x-3">
                <UserCog className="h-4 w-4 text-gray-400 flex-shrink-0" />
                {hasPermission('drivers', 'write') ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-gray-900 whitespace-nowrap">Role:</span>
                    <select
                      value={driver.role === 'actingdriver' ? 'acting_driver' : (driver.role || '')}
                      onChange={(e) => updateRoleMutation.mutate(e.target.value)}
                      disabled={updateRoleMutation.isLoading}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                    >
                      {!driver.role && <option value="">Not set</option>}
                      {DRIVER_ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {updateRoleMutation.isLoading && (
                      <span className="text-xs text-gray-500">Saving...</span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-900">
                    Role: {DRIVER_ROLE_LABELS[driver.role] || 'Not set'}
                  </span>
                )}
              </div>

              {/*
                  Service types — which job(s) this driver may be OFFERED
                  (RIDE and/or ACTING). Kept always visible and editable here,
                  unlike the fuller acting-profile card further down (vehicle
                  handling, transmission, approval, etc.) which only appears
                  once a driver already has ACTING — this is deliberately the
                  ONE place that can grant it in the first place. Needed for
                  the many existing drivers whose serviceTypes predate acting
                  driver and were never granted it after the fact.
              */}
              <div className="flex items-center space-x-3">
                <Car className="h-4 w-4 text-gray-400 flex-shrink-0" />
                {hasPermission('drivers', 'write') ? (
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm text-gray-900 whitespace-nowrap">Service Types:</span>
                    {SERVICE_TYPE_OPTIONS.map((opt) => (
                      <label key={opt.value} className="flex items-center gap-1.5 text-sm text-gray-900">
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={effectiveServiceTypes.includes(opt.value)}
                          disabled={updateServiceTypesMutation.isLoading}
                          onChange={(e) => {
                            const current = effectiveServiceTypes;
                            const next = e.target.checked
                              ? [...new Set([...current, opt.value])]
                              : current.filter((v) => v !== opt.value);
                            if (!next.length) {
                              toast.error('At least one service type is required');
                              return;
                            }
                            /*
                                Ride needs a vehicle of the driver's OWN. Acting does
                                not — an acting driver drives the customer's car — so
                                this only applies to RIDE.

                                The driver app already enforces it: DriverHomeScreen
                                parks a driver with no approved vehicle on
                                DriverVehicleApprovalScreen and stops their background
                                tracking, so they cannot go online. Nothing said so
                                here though — an operator could tick Ride, see it save,
                                and have no idea the driver still cannot take one.

                                Deliberately a WARNING, not a block: ticking Ride before
                                the vehicle is added is a legitimate order of work. This
                                only names what still has to happen.
                            */
                            if (e.target.checked && opt.value === 'RIDE') {
                              const vehicle = driver?.vehicleId;
                              const pendingApproval = vehicle && typeof vehicle === 'object'
                                && vehicle.isApproved === false;
                              const reason = !vehicle
                                ? 'This driver has no vehicle assigned.'
                                : (pendingApproval ? "This driver's vehicle is still pending approval." : null);
                              if (reason) {
                                // Hold the change and ask. Saving happens in the modal.
                                setPendingRideOptIn({ next, reason });
                                return;
                              }
                            }
                            updateServiceTypesMutation.mutate(next);
                          }}
                        />
                        {opt.label}
                      </label>
                    ))}
                    {updateServiceTypesMutation.isLoading && (
                      <span className="text-xs text-gray-500">Saving...</span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-900">
                    Service Types: {effectiveServiceTypes.join(', ')}
                    {!hasExplicitServiceTypes && (
                      <span className="text-xs text-gray-500"> (default)</span>
                    )}
                  </span>
                )}
              </div>

              {/* Driver app + handset, reported by the driver app */}
              <div className="flex items-start space-x-3">
                <Smartphone className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-900 min-w-0">
                  {driver.deviceMeta?.appVersion || driver.deviceMeta?.os ? (
                    <>
                      <div>
                        App: <span className="font-mono">{driver.deviceMeta?.appVersion || 'N/A'}</span>
                        {driver.deviceMeta?.buildNumber && (
                          <span className="text-gray-500"> (build {driver.deviceMeta.buildNumber})</span>
                        )}
                      </div>
                      <div className="text-gray-600">
                        {[
                          driver.deviceMeta?.os && `${driver.deviceMeta.os} ${driver.deviceMeta.osVersion || ''}`.trim(),
                          driver.deviceMeta?.brand && `${driver.deviceMeta.brand} ${driver.deviceMeta.model || ''}`.trim()
                        ].filter(Boolean).join(' - ')}
                      </div>
                    </>
                  ) : (
                    <span>Device: Not reported</span>
                  )}
                </div>
              </div>

              {/* Regional Office Information for Super Admin */}
              {user?.role === 'SUPER_ADMIN' && (
                <div className="flex items-center space-x-3">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">
                    Regional Office: {currentRegionalOffice?.name || 'Not Assigned'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Performance Stats */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Performance</h3>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Rating</span>
              <div className="flex items-center space-x-2">
                <Star className="h-4 w-4 text-yellow-400" />
                <span className="text-sm font-medium text-gray-900">
                  {driver.ratingData?.currentrating?.toFixed(1) || '0.0'}
                </span>
                <span className="text-sm text-gray-500">
                  ({driver.ratingData?.count || 0} reviews)
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Trips Accepted</span>
              <span className="text-sm font-medium text-gray-900">
                {driver.totalTripsAccepted || 0}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Trips Rejected</span>
              <span className="text-sm font-medium text-gray-900">
                {driver.totalTripsRejected || 0}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Earnings</span>
              <span className="text-sm font-medium text-gray-900">
                ₹{driver.driverEarnings?.toFixed(2) || '0.00'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Due Amount</span>
              <span className="text-sm font-medium text-gray-900">
                ₹{driver.driverDue?.toFixed(2) || '0.00'}
              </span>
            </div>

            {driver.dueCycleTrips > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Trips in Due Cycle</span>
                <span className="text-sm font-medium text-gray-900">
                  {driver.dueCycleTrips}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Trips Next Due Date</span>
              <span className="text-sm font-medium text-gray-900">
                {typeof driver.TotalTripsNextDueDate !== 'undefined' ? driver.TotalTripsNextDueDate : (driver.dueCycleTrips || 0)}
              </span>
            </div>

            {driver.nextDueDate && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Next Due Date</span>
                {isEditingDueDate ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="date"
                      value={newDueDate}
                      onChange={(e) => setNewDueDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="text-sm border rounded px-2 py-1"
                    />
                    <button
                      onClick={handleSaveDueDate}
                      disabled={updateDueDateMutation.isLoading}
                      className="text-green-600 hover:text-green-800"
                    >
                      <Save className="h-4 w-4" />
                    </button>
                    <button
                      onClick={handleCancelDueDate}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">
                      {new Date(driver.nextDueDate).toLocaleDateString()}
                    </span>
                    {hasPermission('drivers', 'write') && (
                      <button
                        onClick={handleEditDueDate}
                        className="text-primary-600 hover:text-primary-800"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {driver.deviceMeta && driver.deviceMeta.os && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Operating System</span>
                <span className="text-sm font-medium text-gray-900">
                  {driver.deviceMeta.os.charAt(0).toUpperCase() + driver.deviceMeta.os.slice(1)}
                </span>
              </div>
            )}

            {driver.deviceMeta && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">App Version</span>
                <span className="text-sm font-medium text-gray-900">
                  {driver.deviceMeta.appVersion || 'Unknown'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Vehicle</h3>
          </div>
          <div className="card-body space-y-4">
            {driver.vehicleId ? (
              <>
                <div className="flex items-center space-x-3">
                  <Car className="h-4 w-4 text-gray-400" />
                  {typeof driver.vehicleId === 'object' && driver.vehicleId.regNo ? (
                    hasPermission('vehicles', 'view') ? (
                      <Link
                        to={`/vehicles/${driver.vehicleId._id}?from=driver&driverId=${driver._id}&originalSource=${new URLSearchParams(window.location.search).get('from') || 'drivers'}`}
                        className="text-sm text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
                      >
                        {driver.vehicleId.regNo}
                      </Link>
                    ) : (
                      <span className="text-sm text-gray-900">{driver.vehicleId.regNo}</span>
                    )
                  ) : (
                    hasPermission('vehicles', 'view') ? (
                      <Link
                        to={`/vehicles/${driver.vehicleId}?from=driver&driverId=${driver._id}&originalSource=${new URLSearchParams(window.location.search).get('from') || 'drivers'}`}
                        className="text-sm text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
                      >
                        {driver.vehicleId}
                      </Link>
                    ) : (
                      <span className="text-sm text-gray-900">{driver.vehicleId}</span>
                    )
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Make</span>
                    <span className="text-sm text-gray-900">{driver.vehicleId.make}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Model</span>
                    <span className="text-sm text-gray-900">{driver.vehicleId.model}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Type</span>
                    <span className="text-sm text-gray-900">{driver.vehicleId.type}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Color</span>
                    <span className="text-sm text-gray-900">{driver.vehicleId.color}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Approval Status</span>
                    {typeof driver.vehicleId === 'object' && driver.vehicleId.isApproved !== undefined ? (
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${driver.vehicleId.isApproved
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {driver.vehicleId.isApproved ? 'Approved' : 'Pending Approval'}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-800">
                        Status Unknown
                      </span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <Car className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No vehicle assigned</p>
              </div>
            )}

            {/*
                The driver's OTHER vehicles. The card above shows only the ACTIVE one
                (driver.vehicleId), so a vehicle added through My Vehicles — pending
                approval, approved but not switched to yet, or rejected — was
                invisible on this page. Approving still happens on the vehicle's own
                page; after that the driver asks to switch to it in the app.
            */}
            {Array.isArray(driver.allVehicles) && driver.allVehicles.some(v => !v.isActive) && (
              <div className="pt-4 border-t border-gray-100">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Other vehicles</h4>
                <div className="space-y-3">
                  {driver.allVehicles.filter(v => !v.isActive).map(v => {
                    const badge = {
                      PENDING: { label: 'Pending Approval', cls: 'bg-yellow-100 text-yellow-800' },
                      APPROVED: { label: 'Approved · not active', cls: 'bg-green-100 text-green-800' },
                      REJECTED: { label: 'Rejected', cls: 'bg-red-100 text-red-800' },
                      BLOCKED: { label: 'Blocked', cls: 'bg-red-100 text-red-800' },
                    }[v.status] || { label: v.status, cls: 'bg-gray-100 text-gray-800' };
                    const detail = [v.make, v.model].filter(Boolean).join(' ');
                    return (
                      <div key={v._id} className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          {hasPermission('vehicles', 'view') ? (
                            <Link
                              to={`/vehicles/${v._id}?from=driver&driverId=${driver._id}`}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800"
                            >
                              {v.regNo || 'Vehicle'}
                            </Link>
                          ) : (
                            <span className="text-sm font-medium text-gray-900">{v.regNo || 'Vehicle'}</span>
                          )}
                          <p className="text-xs text-gray-500">
                            {[detail, v.type].filter(Boolean).join(' · ') || '—'}
                          </p>
                          {v.status === 'REJECTED' && v.rejectReason && (
                            <p className="text-xs text-red-600">Reason: {v.rejectReason}</p>
                          )}
                          {v.isParivahanFailed && v.status === 'PENDING' && (
                            <p className="text-xs text-amber-600">RC could not be verified online. Check the documents.</p>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/*
          Acting Driver.

          Shown only for a driver who offers ACTING, because it is a separate
          capability with a SEPARATE approval: the top-level "Approved" badge says
          they may drive for the platform, this one says they are vetted to drive a
          customer's own car. Without this panel an admin had no way to see that a
          driver they had approved was still being refused every acting job, and no
          way to grant it — the accept path and the match engine both require this
          flag, and nothing in the product ever set it.
      */}
      {Array.isArray(driver?.serviceTypes) && driver.serviceTypes.includes('ACTING') && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Acting Driver</h3>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
              driver?.actingProfile?.isApproved
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {driver?.actingProfile?.isApproved ? 'Approved' : 'Pending Approval'}
            </span>
          </div>
          <div className="card-body">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Can drive</span>
                <span className="text-sm text-gray-900">
                  {(driver?.actingProfile?.vehicleClassesCanDrive || []).join(', ') || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Transmission</span>
                <span className="text-sm text-gray-900">
                  {(driver?.actingProfile?.transmissions || []).join(', ') || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Experience</span>
                <span className="text-sm text-gray-900">
                  {driver?.actingProfile?.drivingExperience?.totalExperience || '—'}
                  {driver?.actingProfile?.drivingExperience?.commercialExperience
                    ? ` (commercial ${driver.actingProfile.drivingExperience.commercialExperience})`
                    : ''}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Licence verified</span>
                <span className="text-sm text-gray-900">
                  {driver?.actingProfile?.licenseVerified ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            {/* Service Type now lives once, always-visible, in the driver info
                panel above (next to Role) — it's the only control that can
                GRANT acting in the first place, so it can't live only inside
                this acting-gated card. Kept out of here to avoid showing the
                same toggle twice for a driver who already has it. */}

            {actingApprovalPending && hasPermission('drivers', 'write') && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-3">
                  This driver cannot accept acting jobs until their acting profile is
                  approved. Editing their vehicle handling or driving experience resets
                  this for re-vetting.
                </p>
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isLoading}
                  className="btn btn-success"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Acting Driver
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documents */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Documents</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {driver.documents && Object.entries(driver.documents)
              .filter(([key, url]) => {
                // Only show driver-specific documents, exclude vehicle documents
                const vehicleDocuments = [
                  'vehicleRcDoc',
                  'vehicleRcDocBackSide',
                  'vehiclePhoto',
                  'insurance',
                  'roadTaxDoc',
                  'permitDoc',
                  'fitnessDoc',
                  'pucDoc'
                ];
                return !vehicleDocuments.includes(key);
              })
              .map(([key, url]) => (
                <div key={key} className="border rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </h4>
                  {url ? (
                    <button
                      onClick={() => handleViewDocument(key)}
                      disabled={documentLoading}
                      className={`text-sm bg-transparent border-none cursor-pointer underline ${documentLoading
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-primary-600 hover:text-primary-900'
                        }`}
                    >
                      {documentLoading ? 'Loading...' : 'View Document'}
                    </button>
                  ) : (
                    <span className="text-sm text-gray-500">Not uploaded</span>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Location Map */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Current Location</h3>
        </div>
        <div className="card-body p-0">
          <DriverMap driver={driver} height="300px" />
        </div>
      </div>

      {/* Bank Details */}
      {driver.bankDetails && (
        <div className="card">
          <div className="card-header flex items-center space-x-4">
            <h3 className="text-lg font-medium text-gray-900">Bank Details</h3>
            {driver.isBankVerified && (
              <div
                className={`text-sm bg-green-300 border-none cursor-pointer rounded-full w-max px-3 py-1 text-green-900 font-medium inline-block`}
              >
                Bank Verified
              </div>
            )}
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Account Holder</label>
                <p className="text-sm text-gray-900">{driver.bankDetails.accountHolderName}</p>
              </div>

              <div>
                <label className="form-label">Account Number</label>
                <p className="text-sm text-gray-900">{driver.bankDetails.accountNumber}</p>
              </div>

              <div>
                <label className="form-label">Bank Name</label>
                <p className="text-sm text-gray-900">{driver.bankDetails.bankName}</p>
              </div>

              <div>
                <label className="form-label">IFSC Code</label>
                <p className="text-sm text-gray-900">{driver.bankDetails.ifscCode}</p>
              </div>

              <div>
                <label className="form-label">Branch</label>
                <p className="text-sm text-gray-900">{driver.bankDetails.branch}</p>
              </div>

              <div>
                <label className="form-label">UPI ID</label>
                <p className="text-sm text-gray-900">{driver.bankDetails.UPIID}</p>
              </div>

              {driver.razorpayLinkedAccountDetails?.branch && (
                <div>
                  <label className="form-label">Razorpay Branch</label>
                  <p className="text-sm text-gray-900">{driver.razorpayLinkedAccountDetails.branch}</p>
                </div>
              )}

              {driver.razorpayLinkedAccountDetails?.status && (
                <div>
                  <label className="form-label">Razorpay Status</label>
                  <p className="text-sm text-gray-900">{typeof driver.razorpayLinkedAccountDetails.status === 'string' ? driver.razorpayLinkedAccountDetails.status.charAt(0).toUpperCase() + driver.razorpayLinkedAccountDetails.status.slice(1).toLowerCase() : driver.razorpayLinkedAccountDetails.status}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Block Driver Modal */}
      <BlockDriverModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        driver={driver}
        onBlock={handleBlock}
      />

      {/* Confirm enabling Ride for a driver who cannot yet take one. */}
      {pendingRideOptIn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                Enable Ride service?
              </h3>
              <button
                onClick={() => setPendingRideOptIn(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-700 mb-2">{pendingRideOptIn.reason}</p>
            <p className="text-sm text-gray-500 mb-6">
              A Ride driver brings their own vehicle, so they cannot be offered rides
              until one is added and approved. Acting jobs are unaffected — an acting
              driver drives the customer's car.
            </p>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingRideOptIn(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateServiceTypesMutation.mutate(pendingRideOptIn.next);
                  setPendingRideOptIn(null);
                }}
                className="btn btn-success"
              >
                Enable anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      <HistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        driverId={driver._id}
        driverName={driver.name}
      />

      {/* Regional Office Modal */}
      {showRegionalOfficeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Manage Regional Office</h3>
              <button
                onClick={() => setShowRegionalOfficeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="form-label">Current Regional Office</label>
                <p className="text-sm text-gray-900">
                  {currentRegionalOffice?.name || 'Not Assigned'}
                </p>
              </div>

              <div>
                <label className="form-label">Change to Regional Office</label>
                <select
                  className="input"
                  onChange={(e) => handleUpdateRegionalOffice(e.target.value)}
                  disabled={updateRegionalOfficeMutation.isLoading}
                >
                  <option value="">Select Regional Office</option>
                  {regionalOffices?.map((office) => (
                    <option
                      key={office._id}
                      value={office._id}
                      selected={office._id === driver.regionalOffice}
                    >
                      {office.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowRegionalOfficeModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Driver Modal */}
      {isEditing && editingDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Driver Details</h3>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={editingDriver.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  value={editingDriver.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={editingDriver.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label className="form-label">Aadhar Number</label>
                <input
                  type="text"
                  value={editingDriver.aadharNo}
                  onChange={(e) => handleInputChange('aadharNo', e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label className="form-label">License Number</label>
                <input
                  type="text"
                  value={editingDriver.licenseNo}
                  onChange={(e) => handleInputChange('licenseNo', e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label className="form-label">PAN Number</label>
                <input
                  type="text"
                  value={editingDriver.panNo}
                  onChange={(e) => handleInputChange('panNo', e.target.value)}
                  className="input"
                />
              </div>

              <div>
                <label className="form-label">Gender</label>
                <select
                  value={editingDriver.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className="input"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCancelEdit}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDriver}
                disabled={updateDriverMutation.isLoading}
                className="btn btn-primary"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showDriverJsonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Driver Details (JSON)</h3>
              <button
                onClick={() => setShowDriverJsonModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4 text-sm text-gray-700">
              <p>Full driver object (JSON).</p>
            </div>
            <div className="bg-gray-100 p-4 rounded overflow-auto" style={{ maxHeight: '60vh' }}>
              <pre className="text-xs">{JSON.stringify(driver, null, 2)}</pre>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowDriverJsonModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={showDocumentModal}
        onClose={handleCloseDocumentModal}
        documentUrl={currentDocument?.url}
        documentType={currentDocument?.type}
        originalUrl={currentDocument?.originalUrl}
      />
    </div>
  );
};

export default DriverDetails; 